import { useState } from "react";
import { Delete } from "lucide-react";

import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import type { StaffMember } from "@workspace/venue-management-staff";

// HA-831 part 2 — the EPOS staff switch asks for the staff member's PIN. A tap on a name used to be
// enough to become them, the Owner included.
//
// HA-831-3 — with its own keypad, like the check-in screen: a till is a touchscreen, and a bare text
// field relied on the device's on-screen keyboard showing up. Typing still works on a till with a
// keyboard. The fourth digit submits.

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function StaffPinDialog({
	staff,
	error,
	isSubmitting,
	onSubmit,
	onCancel,
}: {
	staff: StaffMember | null;
	error?: string;
	isSubmitting?: boolean;
	onSubmit: (pin: string) => void;
	onCancel: () => void;
}) {
	const [pin, setPin] = useState("");

	const submit = (value: string) => {
		if (value.length !== 4 || isSubmitting) return;
		onSubmit(value);
		setPin("");
	};

	const change = (value: string) => {
		const next = value.replace(/\D/g, "").slice(0, 4);
		setPin(next);
		if (next.length === 4) submit(next);
	};

	const cancel = () => {
		setPin("");
		onCancel();
	};

	return (
		<Dialog
			open={staff !== null}
			onOpenChange={(open) => {
				if (!open) cancel();
			}}
		>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>Enter your PIN</DialogTitle>
					<DialogDescription>
						{staff
							? `${staff.personalDetails.preferredName}, enter your 4-digit PIN to use the till.`
							: null}
					</DialogDescription>
				</DialogHeader>
				<Input
					aria-label="PIN"
					type="password"
					inputMode="numeric"
					autoComplete="off"
					autoFocus
					maxLength={4}
					value={pin}
					disabled={isSubmitting}
					onChange={(e) => change(e.target.value)}
				/>
				<div
					className="grid grid-cols-3 gap-2"
					role="group"
					aria-label="PIN keypad"
				>
					{DIGITS.map((digit) => (
						<Button
							key={digit}
							type="button"
							variant="outline"
							className="h-14 text-xl"
							disabled={isSubmitting}
							onClick={() => change(pin + digit)}
						>
							{digit}
						</Button>
					))}
					<span />
					<Button
						type="button"
						variant="outline"
						className="h-14 text-xl"
						disabled={isSubmitting}
						onClick={() => change(pin + "0")}
					>
						0
					</Button>
					<Button
						type="button"
						variant="outline"
						className="h-14"
						aria-label="Delete last digit"
						disabled={isSubmitting || pin.length === 0}
						onClick={() => setPin(pin.slice(0, -1))}
					>
						<Delete className="size-5" />
					</Button>
				</div>
				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}
				<DialogFooter>
					<Button variant="outline" onClick={cancel}>
						Cancel
					</Button>
					<Button
						onClick={() => submit(pin)}
						disabled={pin.length !== 4 || isSubmitting}
					>
						Continue
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
